import * as React from 'react'
import { flushSync } from 'react-dom'
import styled, { createGlobalStyle, css } from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

import { CKEditor, CKEditorConfig } from './CKEditor'

export type WysiwygProps = {
  /** @deprecated this ref is only there temporarily (compat with legacy code). Do not use for any new code */
  editorRef?: React.Ref<CKEditor>
  error?: React.ReactNode
  /** @deprecated this ref is only there temporarily (compat with legacy code). Do not use for any new code */
  textareaRef?: React.Ref<HTMLTextAreaElement>
} & Partial<CKEditorConfig> &
  Omit<JSX.IntrinsicElements['textarea'], 'onChange' | 'onBlur' | 'onFocus' | 'onError'>

const CKEditorGlobalStyle = createGlobalStyle`
  .ck .ck-button__toggle .ck-button__toggle__inner {
    display: block;
  }

  :root {
    // We lower CKEditor modal z-index. It is used for the sticky toolbar behaviour and native dropdowns.
    // In both cases, being set at > 1000 by default goes above our header and modal elements.
    --ck-z-modal: 200 !important;
  }
`

function WysiwygComponent({
  className,
  disabled,
  data = '',
  error,
  id,
  initialConfig,
  onBlur,
  onChange = () => {},
  onDestroy,
  onError,
  onFocus,
  onReady,
  editorRef,
  textareaRef,
  ...textareaProps
}: WysiwygProps): React.ReactElement<WysiwygProps> {
  const ownTextareaRef = React.useRef<HTMLTextAreaElement>(null)
  const ckeditorRef = React.useRef<CKEditor>()

  // With React 18, all state updates are batched. CKEditor doesn't especially like
  // receiving all these updates at once. To address this, we use `flushSync`: this
  // effectively disables batching for a given code portion.
  // Thus, batching will be disabled for state updates done within the `onChange` handler.
  const debouncedChange = plume.hooks.useDebounce(
    (...args: Parameters<typeof onChange>) => flushSync(() => onChange(...args)),
    false,
  )
  const { classList, status } = plume.behaviours.getStatusClassName(className, error)

  // We use refs to allow rebinding CKEditor events to new handler. Event
  // handlers are bound to CKEditor once on mount, but they call the
  // current value of the ref. By updating the ref, we update the handler
  // of CKEditor event
  const handlersRef = React.useRef({
    onBlur,
    onDestroy,
    onChange: debouncedChange,
    onError,
    onFocus,
    onReady,
  })
  handlersRef.current = {
    onBlur,
    onDestroy,
    onChange: debouncedChange,
    onError,
    onFocus,
    onReady,
  }

  React.useEffect(() => {
    env.initUserConfig().then(() => {
      const userConfig = env.getUserConfig()

      if (ownTextareaRef.current !== null) {
        CKEditor.create(ownTextareaRef.current, {
          data,
          disabled,
          initialConfig: {
            language: userConfig.lang,
            ...initialConfig,
            initialData: initialConfig?.initialData ?? data ?? '',
          },
          onBlur(event, editor) {
            if (handlersRef.current.onBlur) {
              handlersRef.current.onBlur(event, editor)
            }
          },
          onChange(event, editor) {
            if (handlersRef.current.onChange) {
              handlersRef.current.onChange(event, editor)
            }
          },
          onDestroy(editor) {
            if (handlersRef.current.onDestroy) {
              handlersRef.current.onDestroy(editor)
            }
          },
          onError(error, meta) {
            console.error('[Wysiwyg]', error, meta)
            if (handlersRef.current.onError) {
              handlersRef.current.onError(error, meta)
            }
          },
          onFocus(event, editor) {
            if (handlersRef.current.onFocus) {
              handlersRef.current.onFocus(event, editor)
            }
          },
          onReady(editor) {
            if (handlersRef.current.onReady) {
              handlersRef.current.onReady(editor)
            }
          },
        }).then((ckeditor) => {
          if (ckeditorRef.current) {
            ckeditorRef.current.destroy()
          }
          ckeditorRef.current = ckeditor

          if (typeof editorRef === 'function') {
            editorRef(ckeditor)
          } else if (editorRef) {
            //@ts-expect-error: we can assign to a ref in this context even if it
            //is readonly since we are the only ones that can give it a value.
            editorRef.current = ckeditor
          }

          // CKEditor hides the textarea with a display:none, making it
          // unfocusable. This means that clicking on the label will never focus
          // the textarea (that should forward focus to the ckeditor). Instead,
          // after CKEditor init, we override the "hide" styles of the textarea to
          // have it hidden, but focusable
          if (ownTextareaRef.current) {
            ownTextareaRef.current.style.display = 'block'
            ownTextareaRef.current.style.height = '0'
            ownTextareaRef.current.style.padding = '0'
            ownTextareaRef.current.style.margin = '0'
            ownTextareaRef.current.style.border = 'none'
          }
        })
      }
    })

    return () => {
      if (ckeditorRef.current) {
        ckeditorRef.current.destroy()
      }
    }
  }, [id])

  React.useEffect(() => {
    if (ckeditorRef.current && ckeditorRef.current.getData() !== data) {
      ckeditorRef.current.setData(data)
    }
  }, [data])

  React.useEffect(() => {
    if (ckeditorRef.current) {
      ckeditorRef.current.setDisabled(disabled)
    }
  }, [disabled])

  return (
    <>
      <CKEditorGlobalStyle />
      <div className={classList.filter(Boolean).join(' ')}>
        <textarea
          id={id}
          onFocus={() => {
            ckeditorRef.current?.focus()
          }}
          ref={(ref) => {
            // @ts-expect-error: we set a readonly property because we need to assign a value to the ref here
            ownTextareaRef.current = ref
            if (typeof textareaRef === 'function') {
              textareaRef(ref)
            } else if (textareaRef) {
              // @ts-expect-error: we set a readonly property because we need to assign a value to the ref here
              textareaRef.current = ref
            }
          }}
          {...textareaProps}
        />
        {status === 'error' && <plume.styles.field.Message type="error">{error}</plume.styles.field.Message>}
      </div>
    </>
  )
}

export const Wysiwyg = styled(WysiwygComponent)`
  ${({ error }) => {
    if (error) {
      return css`
        .ck .ck-content {
          border: 1px dashed ${plume.COLORS.PRIMARY_RED} !important;
        }
      `
    }
  }}
  .ck .ck-read-only {
    background-color: ${plume.COLORS.GREY_SHADE_6};
    color: ${plume.COLORS.GREY_SHADE_3};
    cursor: not-allowed;
  }

  // HACK: See https://ulule-team.atlassian.net/browse/FO-544.
  .ck .ck-widget .ck-widget__type-around__button {
    position: absolute;

    svg {
      position: absolute;
    }
  }

  .ck .ck-read-only {
    background-color: ${plume.COLORS.GREY_SHADE_6};
    color: ${plume.COLORS.GREY_SHADE_3};
    cursor: not-allowed;
  }

  .ck-editor .ck-editor__editable .ck-list-bogus-paragraph {
    display: inline;
  }
`
